<script setup lang="ts">
// #719 観点B: モバイルナビ背景。デスクトップと同じ 3 レイヤ（下=海1/海2＝わずかに
// 動く・上=陸地）を敷いて「ここはワールドマップ」を伝える。
import encodiaSea1 from '@/assets/images/map/AreaMap_Encodia_Sea1.webp'
import encodiaSea2 from '@/assets/images/map/AreaMap_Encodia_Sea2.webp'
import encodiaLand from '@/assets/images/map/AreaMap_Encodia_Land.webp'

const runtimeConfig = useRuntimeConfig()

useSeoMeta({
  title: 'エンコーディア（ワールドマップ） | Code & Magic',
  description: 'エンコーディアのワールドマップです。',
  ogTitle: 'エンコーディア（ワールドマップ） | Code & Magic',
  ogDescription: 'エンコーディアのワールドマップです。',
  ogImage: `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
  twitterCard: 'summary_large_image',
})

definePageMeta({
  // #719 観点B: レイアウトは template 側の <NuxtLayout :name> で SSR から確定させる
  // （useIsMobile 分岐。setPageLayout は使わない — responsive_sizing.md 参照）。
  middleware: ['authenticated', 'confirm-game-state'],
  layout: false,
  usePageReady: true,
})

const { isMobile } = useIsMobile()

const { listVisibleChildren, coordX, coordY, isSceneVisible, isSceneMarked, isSceneCleared } = useSceneAccess()
const { getSparkles } = useSocialApi()

const { finishLoading } = usePageReady()

const visibleChildrenIds = computed(() => {
  return listVisibleChildren('wld_encodia').map(scene => scene.sceneId)
})

const sparkleData = ref<Record<string, number>>({})

onMounted(async () => {
  await finishLoading()

  // Fetch sparkle tiers in background (non-blocking)
  getSparkles()
    .then((res: { sparkles: Record<string, number> }) => { sparkleData.value = res.sparkles })
    .catch(() => { /* silent fail — sparkles are decorative */ })
})
</script>

<template>
  <NuxtLayout :name="isMobile ? 'mobile-nav' : 'centering43'">
    <SceneMap
      scene-id="wld_encodia"
      back-to-text="冒険者のキャンプに戻る"
      :mobile="isMobile"
    >
      <!-- モバイル背景: 動く海 + 陸地の 3 レイヤ（デスクトップと同じ絵） -->
      <template #mobile-background>
        <div class="world-mobile-bg">
          <div
            class="world-mobile-bg__sea world-mobile-bg__sea1"
            :style="{ backgroundImage: `url(${encodiaSea1})` }"
          />
          <div
            class="world-mobile-bg__sea world-mobile-bg__sea2"
            :style="{ backgroundImage: `url(${encodiaSea2})` }"
          />
          <div
            class="world-mobile-bg__land"
            :style="{ backgroundImage: `url(${encodiaLand})` }"
          />
        </div>
      </template>

      <template #map>
        <div class="map-layers">
          <v-img
            class="sea-layer sea-layer-1"
            src="@/assets/images/map/AreaMap_Encodia_Sea1.webp"
            width="100%"
            height="auto"
            lazy-src="@/assets/images/map/LazyAreaMap_Encodia.png"
            :transition="false"
            aria-hidden="true"
          />
          <v-img
            class="sea-layer sea-layer-2"
            src="@/assets/images/map/AreaMap_Encodia_Sea2.webp"
            width="100%"
            height="auto"
            :transition="false"
            aria-hidden="true"
          />
          <v-img
            class="land-layer"
            src="@/assets/images/map/AreaMap_Encodia_Land.webp"
            width="100%"
            height="auto"
            :transition="false"
          />
        </div>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 1024 768"
          version="1.1"
          xmlns="http://www.w3.org/2000/svg"
          xpmlns:xlink="http://www.w3.org/1999/xlink"
          xml:space="preserve"
          xmlns:serif="http://www.serif.com/"
          style="
          position: absolute;
          top: 0;
          left: 0;
          fill-rule: evenodd;
          clip-rule: evenodd;
          stroke-linecap: round;
          stroke-linejoin: round;
          stroke-miterlimit: 1.5;
        "
        >
          <!-- Place a blinking red small circle on visible scenes.
             認証依存で件数が変わる装飾マーカー（点滅リング / キラキラ / ×マーク）。
             SSR では scenes が空で 0 件、CSR で reloadGameState() 後に N 件へ変化する。
             この可変長 v-for が静的クリック領域 <path> の兄弟だと、hydration 後の
             reactive patch が両者を取り違え先頭 2 本の <path> を重複ノードに化けさせた
             （#714）。マーカーは 100% クライアント状態依存で SSR 価値が無いため
             ClientOnly で SSR から外し、静的 <path> と hydration 境界を分離する
             （UserInfoCard と同じ #596 系の対処）。 -->
          <ClientOnly>
            <g
              v-for="sceneId in visibleChildrenIds"
              :key="sceneId"
            >
              <!-- A mark for enterable areas -->
              <template v-if="isSceneCleared(sceneId)">
                <!-- a static circle for cleared areas -->
                <circle
                  :cx="coordX(sceneId)"
                  :cy="coordY(sceneId)"
                  r="6"
                  fill="#4BBF81"
                  stroke="#FFFFFF"
                  stroke-width="1.5"
                  opacity="0.9"
                />
              </template>
              <template v-else-if="isSceneVisible(sceneId)">
                <!-- a pulse ring for not cleared areas -->
                <circle
                  :cx="coordX(sceneId)"
                  :cy="coordY(sceneId)"
                  :r="6"
                  fill="#B11226"
                />
                <circle
                  :cx="coordX(sceneId)"
                  :cy="coordY(sceneId)"
                  r="8"
                  fill="none"
                  stroke="#F5E5AA"
                  stroke-width="2"
                  opacity="0.95"
                >
                  <animate
                    attributeName="r"
                    values="8;18"
                    dur="2.4s"
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="opacity"
                    values="0.8;0"
                    dur="2.4s"
                    repeatCount="indefinite"
                  />
                </circle>
              </template>

              <!-- キラキラマーク (攻略活性度) -->
              <SparkleIndicator
                v-if="sparkleData[sceneId]"
                :tier="sparkleData[sceneId]"
                :cx="coordX(sceneId)"
                :cy="coordY(sceneId)"
              />

              <!-- ×マーク -->
              <g
                v-if="isSceneMarked(sceneId)"
                :transform="`translate(${coordX(sceneId)} ${coordY(sceneId)}) translate(-20 -18) scale(0.07)`"
              >
                <path
                  d="m629.76 61.296 15.824 13.316c-1.3928 3.2384-4.9373 6.8389-10.592 10.875-2.8273 1.208-4.2618 2.4411-4.2618 3.6492 0 .38648.71728 1.402 2.1099 3.0208-90.85 52.442-167.9 101.64-231.32 147.61 85.238 64.549 137.31 101.5 156.34 110.73-3.5446 11.31-11.266 19.986-23.25 26.027-4.22 2.0304-9.4946 4.4229-15.866 7.2498-11.225 5.2924-16.837 8.0956-16.837 8.5307v.55609l3.1646 6.6457c-2.8691.79727-5.359 2.2236-7.4265 4.2289-7.7645 5.268-16.246 8.9172-25.36 10.923-2.8272 0-6.3292-1.208-10.634-3.6492-2.7848-1.1601-6.2875-2.61-10.507-4.2288-4.2618-1.6188-6.2874-2.4411-6.2874-2.4411-.7598-.38648-1.4346-.604-2.1519-.604 0 0-1.0972.43509-3.2072 1.2323-14.769 10.512-28.146 17.158-40.13 19.986-2.11 0-4.9372-.60403-8.4393-1.8364-22.533-13.268-46.459-30.644-71.819-52.007-21.098-18.149-36.922-31.102-47.556-38.763l-10.507 8.4585-72.916 54.496c-9.1566 6.8632-35.192 27.453-78.191 61.698h-13.756l-20.128-12.663c1.4345-3.2384 2.1943-4.8572 2.1943-4.8572 1.3927-2.4168 2.0675-3.6005 2.0675-3.6005 0-2.4168-2.11-3.6492-6.3293-3.6492s-10.549 1.2567-18.989 3.6492c-.75978 0-2.8272.57965-6.3293 1.8128-7.7645-2.4412-19.031-10.512-33.842-24.191-4.22-3.6005-6.2874-6.066-6.2874-7.274 0-.79724 1.7726-2.6343 5.2746-5.462 4.9372-4.4708 7.3846-8.0721 7.3846-10.875 0-.79724-3.502-5.5099-10.549-13.92-3.502-6.066-5.2321-9.0625-5.2321-9.0625l51.734-38.135 115.16-76.198c2.8273-2.4648 5.6545-4.4708 8.4393-6.066-45.742-45.578-83.044-90.746-111.95-135.55-6.3293-10.899-10.169-18.56-11.604-22.982 9.8738-8.8693 22.871-17.786 39.117-26.656 7.0472 3.2384 12.279 5.6788 15.824 7.274 2.7848 2.8276 5.612 4.2289 8.4393 4.2289 7.0472 0 11.604-2.9965 13.714-9.0625 2.1518-11.31 10.929-17.158 26.458-17.545 4.9373 0 11.604 2.61 20.044 7.8301 44.433 41.567 87.053 78.469 127.82 110.76 91.652-57.686 167.27-102.25 227.19-133.74 11.984-6.0417 18.609-9.0625 20.086-9.0625 3.5021 0 14.094 5.0269 31.732 15.104l6.2876 4.8572c0 .79723-1.0547 2.1992-3.1648 4.2532-1.3926.79726-2.0674 1.5952-2.0674 2.3925 0 1.208 6.2874 4.4464 18.904 9.7152l15.908-2.4168 6.2849 5.3897v8e-6z"
                  fill="#f00"
                />
              </g>
            </g>
          </ClientOnly>

          <path
            data-scene-id="wld_beginnersPort"
            d="M592.556,635.523c0.503,-11.9 3.625,-23.139 11.949,-27.301c21.278,-10.639 26.853,-18.594 51.733,-15.55c15.025,1.838 20.579,33.038 8.235,61.36c-2.26,5.185 -26.361,16.214 -33.556,15.614c-7.583,-0.631 -15.235,-2.518 -22.361,-4.3c-4.932,-1.233 -16.927,-7.911 -16,-29.823Z"
            :class="`scene ${isSceneVisible('wld_beginnersPort') ? '' : 'd-none'}`"
          />
          <path
            data-scene-id="wld_slumberingBeach"
            d="M412.176,583.531c23.761,-8.353 36.138,-4.16 44.48,1.463c9.86,6.648 15.341,23.657 5.886,54.05c-2.885,9.271 -21.858,36.067 -40.891,37.148c-7.347,0.418 -29.979,-14.337 -36.499,-35.136c-6.729,-21.462 3.126,-49.123 27.024,-57.525Z"
            fill="url(#shine)"
            :class="`scene ${isSceneVisible('wld_slumberingBeach') ? '' : 'd-none'}`"
          />
          <path
            data-scene-id="wld_crystalWaveIsland"
            d="M266.115,668.185c-12.973,-18.321 -16.333,-69.186 -3.61,-90.288c10.747,-17.825 37.393,-19.794 41.254,-20.818c6.773,-1.796 23.578,-3.141 40.481,9.634c10.59,8.004 25.501,41.976 26.305,45.41c0.945,4.033 2.768,34.478 2.768,41.092c-0,5.699 -3.558,25.908 -9.294,33.809c-7.658,10.549 -15.698,13.547 -20.873,14.341c-13.944,2.143 -62.331,-12.421 -77.031,-33.18Z"
            :class="`scene ${isSceneVisible('wld_crystalWaveIsland') ? '' : 'd-none'}`"
          />
          <path
            data-scene-id="wld_silverLeafForest"
            d="M547.521,568.68c3.262,-10.696 5.834,-18.787 19.476,-24.984c11.119,-5.051 14.754,-6.743 36.144,-5.923c41.458,1.591 66.72,34.241 58.321,46.477c-5.946,8.663 -15.423,10.431 -23.665,14.552c-1.527,0.763 -32.023,13.669 -38.702,16.341c-6.523,2.609 -40.488,-2.294 -48.087,-15.147c-6.443,-10.898 -3.308,-31.137 -3.487,-31.316Z"
            :class="`scene ${isSceneVisible('wld_silverLeafForest') ? '' : 'd-none'}`"
          />
        </svg>
      </template>
    </SceneMap>
  </NuxtLayout>
</template>

<style scoped>
.map-layers {
  position: relative;
  width: 100%;
  /* clip drift overflow so animation never escapes the 4:3 area */
  overflow: hidden;
  /* keep aspect ratio while sea image is loading */
  aspect-ratio: 1024 / 768;
  /* let clicks pass through to the SVG scene paths beneath */
  pointer-events: none;
}

.map-layers .sea-layer {
  position: absolute;
  inset: 0;
  /* scale-up so larger drift still never reveals the edge */
  transform-origin: center;
  will-change: transform, opacity;
}

/* Sea1: base layer — horizontal drift only */
.map-layers .sea-layer-1 {
  animation: sea-drift-x 11s ease-in-out infinite alternate;
}

/* Sea2: overlay — alpha-blended fade, drifts with base to avoid ghosting */
.map-layers .sea-layer-2 {
  animation:
    sea-drift-x 11s ease-in-out infinite alternate,
    sea-fade 7s ease-in-out infinite alternate;
}

.map-layers .land-layer {
  position: absolute;
  inset: 0;
}

/* #719 観点B: モバイルナビ背景の 3 レイヤ（cover 敷き）。海はデスクトップと同じ
   キーフレームでわずかにドリフト。陸地が上でクリップ用に overflow:hidden。 */
.world-mobile-bg {
  position: absolute;
  inset: 0;
  overflow: hidden;
}

.world-mobile-bg__sea,
.world-mobile-bg__land {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
}

.world-mobile-bg__sea1 {
  animation: sea-drift-x 11s ease-in-out infinite alternate;
}

.world-mobile-bg__sea2 {
  animation:
    sea-drift-x 11s ease-in-out infinite alternate,
    sea-fade 7s ease-in-out infinite alternate;
}

@keyframes sea-drift-x {
  from {
    transform: scale(1.06) translate3d(-12px, 0, 0);
  }

  to {
    transform: scale(1.06) translate3d(12px, 0, 0);
  }
}

@keyframes sea-fade {
  from {
    opacity: 0;
  }

  to {
    opacity: 0.3;
  }
}

@media (prefers-reduced-motion: reduce) {
  .map-layers .sea-layer {
    animation: none;
    transform: none;
    filter: none;
  }
}

.pulse-circle {
  transform-origin: center;
  animation: pulse 2.4s ease-in-out infinite;
}

@keyframes pulse {
  0% {
    r: 7;
    opacity: 1;
  }

  50% {
    r: 9;
    opacity: 0.9;
  }

  100% {
    r: 7;
    opacity: 1;
  }
}
</style>
